import { Hand, Mic, MicOff, Video, VideoOff, MonitorUp, ShieldAlert, MoreVertical, Crown, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import useWebinarStore from '@/stores/webinarStore';

const roleIcons = {
  host: <Crown className="w-3 h-3 text-amber-400" />,
  'co-host': <Shield className="w-3 h-3 text-cyan-400" />,
  moderator: <ShieldAlert className="w-3 h-3 text-violet-400" />,
};

const roleLabels = {
  host: 'Hôte',
  'co-host': 'Co-hôte',
  moderator: 'Modérateur',
  participant: 'Participant',
};

export default function ParticipantsList({ currentUser }) {
  const { participants, raisedHands, myRole, socket } = useWebinarStore();
  const isHost = ['host', 'co-host'].includes(myRole);

  // Sort: hosts first, then raised hands, then alphabetical
  const sorted = [...participants].sort((a, b) => {
    const roleOrder = { host: 0, 'co-host': 1, moderator: 2, participant: 3 };
    const aOrder = roleOrder[a.role] ?? 3;
    const bOrder = roleOrder[b.role] ?? 3;
    if (aOrder !== bOrder) return aOrder - bOrder;

    const aHand = raisedHands.includes(a.user_id) ? 0 : 1;
    const bHand = raisedHands.includes(b.user_id) ? 0 : 1;
    if (aHand !== bHand) return aHand - bHand;

    return (a.user_name || '').localeCompare(b.user_name || '');
  });

  const handleKick = (userId) => {
    socket?.emit('webinar:kick', { user_id: userId });
  };

  const handleMuteAll = () => {
    socket?.emit('webinar:mute-all');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-sm font-semibold text-white">
          Participants <span className="text-zinc-500 font-normal">({participants.length})</span>
        </span>
        {isHost && (
          <Button variant="ghost" size="sm" onClick={handleMuteAll}
            className="h-7 text-xs text-zinc-400 hover:text-zinc-200 gap-1">
            <MicOff className="w-3 h-3" /> Muter tous
          </Button>
        )}
      </div>

      {/* Raised hands section */}
      {raisedHands.length > 0 && (
        <div className="px-3 py-2 border-b border-zinc-800 bg-amber-500/5">
          <span className="text-[10px] uppercase tracking-wider text-amber-500 font-semibold flex items-center gap-1">
            <Hand className="w-3 h-3" /> Mains levées ({raisedHands.length})
          </span>
        </div>
      )}

      {/* List */}
      <ScrollArea className="flex-1">
        <div className="px-2 py-1">
          {sorted.map(p => {
            const isMe = p.user_id === currentUser?.id;
            const hasHand = raisedHands.includes(p.user_id);
            const initials = (p.user_name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

            return (
              <div key={p.user_id}
                className={`flex items-center gap-2.5 px-2 py-2 rounded-lg transition-colors ${
                  hasHand ? 'bg-amber-500/5' : 'hover:bg-zinc-800/50'
                }`}
              >
                {/* Avatar */}
                <div className="relative">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-600 to-violet-600 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white">{initials}</span>
                  </div>
                  {/* Online indicator */}
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-zinc-900" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-white truncate font-medium">
                      {p.user_name}{isMe && ' (Vous)'}
                    </span>
                    {roleIcons[p.role]}
                    {hasHand && <Hand className="w-3.5 h-3.5 text-amber-400 animate-bounce" />}
                  </div>
                  <span className="text-[10px] text-zinc-500">{roleLabels[p.role] || 'Participant'}</span>
                </div>

                {/* Media status */}
                <div className="flex items-center gap-1">
                  {p.is_muted !== false ? (
                    <MicOff className="w-3.5 h-3.5 text-red-400/60" />
                  ) : (
                    <Mic className="w-3.5 h-3.5 text-emerald-400/60" />
                  )}
                  {p.is_camera_on ? (
                    <Video className="w-3.5 h-3.5 text-emerald-400/60" />
                  ) : (
                    <VideoOff className="w-3.5 h-3.5 text-zinc-600" />
                  )}
                  {p.is_screen_sharing && <MonitorUp className="w-3.5 h-3.5 text-cyan-400" />}
                </div>

                {/* Host actions */}
                {isHost && !isMe && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-zinc-300 opacity-0 group-hover:opacity-100">
                        <MoreVertical className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                      <DropdownMenuItem onClick={() => handleKick(p.user_id)} className="text-red-400 focus:bg-zinc-800">
                        Éjecter
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
